import { formatISO } from 'date-fns'
import { useMemo } from 'react'
import { Schemas } from '~/api/gen'
import { Button } from '~/components/elements/Button'
import { Divider } from '~/components/elements/Divider'
import {
  usePostAffiliates,
  useReadyAffiliate,
} from '~/features/shared/affiliate/hooks'
import { generateBuyButtonUrl } from '~/features/shared/item/lib/generateBuyButtonUrl'
import { generateItemDetailSearchButtonUrl } from '~/features/shared/item/lib/generateSearchButtonUrl'
import {
  sendBuyButtonGtmEvent,
  sendItemDetailSearchButtonGtmEvent,
} from '~/features/shared/item/lib/sendGtmEvent'
import { buildUrl } from '~/features/shared/search/coordinate/builder/buildUrl'
import { useBusinessEventLogging } from '~/hooks/useBusinessEventLogging'
import { useCoordinateById } from '~/hooks/useCoordinateById'
import { useCurrentMember } from '~/hooks/useCurrentMember'
import { getMallType } from '~/lib/getMallType'
import {
  getItemKidForPurchaseItem,
  getItemKidForSearch,
} from '~/lib/zozotown/getKid'
import { isTransitionFromCoordinate } from '~/lib/zozotown/isTransitionFromCoordinate'
import { Mall } from '~/types/mall'
import { ScreenIds } from '~/types/screenIds'

type Props = {
  item: Schemas.item_large
}

/* eslint-disable complexity */
export const PurchaseDetail: React.FC<Props> = ({ item }) => {
  //クーポンの値段表示「¥1,100」の取得
  const displayDiscountAmount = useMemo(() => {
    const [mostValuableCoupon] = item.purchase_items
      .map((purchaseItem) => purchaseItem.coupon)
      .filter((coupon) => coupon !== null)
      .sort((a, b) => b.discount_amount - a.discount_amount)
    return (mostValuableCoupon?.discount_amount ?? 0) <= 0
      ? undefined
      : mostValuableCoupon?.display_discount_amount
  }, [item])

  //購入ボタン、探すボタン用のデータ取得
  const { screenId, objectId } = useReadyAffiliate()
  const { sendLog, uuid } = useBusinessEventLogging()
  const member = useCurrentMember()
  const { postAffiliates } = usePostAffiliates()
  const coordinate = useCoordinateById(
    isTransitionFromCoordinate(screenId ?? ScreenIds.SP, objectId)
      ? objectId
      : 0
  )?.coordinate_wrapper?.coordinate
  const timestamp = formatISO(new Date())

  //探すボタンのURL取得
  const searchButtonUrl = item.id
    ? generateItemDetailSearchButtonUrl({
        searchConditionUrl: item.search_condition_url ?? '',
        itemId: item.id,
        screenId: screenId ?? ScreenIds.SP,
        objectId,
        kid: Number(getItemKidForSearch(screenId, objectId)),
        uuid,
      })
    : undefined

  return (
    <section className="xl:w-[414px] xl:rounded-[3px] xl:border xl:border-gray-300 xl:bg-white xl:pb-[23px] xl:pl-[23px] xl:pt-5">
      <div className="xl:pr-[23px]">
        {item.brand.id !== null && item.brand.key !== undefined ? (
          <p className="text-sm leading-[1.8] xl:text-[13px] xl:leading-[1.6]">
            <span className="hidden xl:inline">ブランド:</span>
            <a
              href={buildUrl({
                snapItems: [
                  {
                    brand: {
                      id: item.brand.id,
                      key: item.brand.key,
                      name: item.brand.name,
                    },
                  },
                ],
              })}
              className="text-blue-500 xl:text-gray-600 xl:hover:underline"
            >
              {item.brand.name}
            </a>
          </p>
        ) : null}
        <h1 className="text-sm leading-[1.8] text-black-400 xl:pt-[9px] xl:text-[18px] xl:leading-normal">
          {item.name}
        </h1>
        {item.aggregate &&
          item.aggregate.coordinate_count > 0 &&
          item.id !== null && (
            <p className="hidden xl:block xl:pt-[3px]">
              <a
                href={`/coordinate/?item_id=${item.id}`}
                className="text-[12px] text-blue-500 hover:underline"
              >
                {item.aggregate.coordinate_count}件の着用コーディネート
              </a>
            </p>
          )}
        <p className="flex items-center text-[20px] font-bold xl:pt-1 xl:text-[34px] xl:font-normal xl:leading-[1.6]">
          {item.display_price}
          {displayDiscountAmount && (
            <span className="ml-[7px] mt-[1px] inline-block rounded-[2px] bg-orange-500 py-[2px] pl-[7px] pr-[6px] align-middle text-[11px] font-normal leading-[1.2] text-white xl:mb-[-1px] xl:rounded-[2px] xl:px-2 xl:py-1 xl:text-[14px]">
              {displayDiscountAmount}
              <span className="text-[10px] xl:text-[13px]">クーポン</span>
            </span>
          )}
        </p>
        {displayDiscountAmount && (
          <p className="mt-[1px] pb-2 text-[12px] text-orange-500 xl:mt-[-2px] xl:pb-[21px] xl:text-[14px] xl:leading-[1.6]">
            (この商品は最大{displayDiscountAmount}クーポンが使えます)
          </p>
        )}
      </div>
      <div className="hidden xl:block xl:pt-[18px]">
        <Divider />
      </div>
      <div className="empty:hidden xl:pr-[23px] xl:pt-3">
        {item.purchase_items.map((purchaseItem) => {
          if (!purchaseItem.purchase_url || item.id === null) return

          const isZozotownItem =
            getMallType(purchaseItem.shop) === Mall.Zozotown

          //購入ボタンのURL取得
          const buyButtonUrl = generateBuyButtonUrl({
            purchaseItem,
            kid: Number(getItemKidForPurchaseItem(screenId, objectId)),
            brandName: item.brand.name,
            itemId: item.id,
            screenId: screenId ?? ScreenIds.SP,
            objectId,
            uuid,
          })

          //クーポンの値段表示
          const coupon = purchaseItem.coupon
            ? ` / ${purchaseItem.coupon.display_discount_amount}クーポン`
            : ''
          return (
            <div
              className="pt-[10px] xl:pt-3"
              key={purchaseItem.purchase_item_id}
            >
              <Button
                href={buyButtonUrl}
                onClick={() => {
                  if (item.id !== null && buyButtonUrl !== undefined)
                    sendBuyButtonGtmEvent({
                      purchaseItem,
                      brandName: item.brand.name,
                      itemId: item.id,
                      buyButtonUrl,
                      kid: getItemKidForPurchaseItem(screenId, objectId),
                    })

                  postAffiliates({
                    before_screen_id: Number(screenId ?? ScreenIds.SP),
                    content_id: objectId,
                    item_id: item.id ?? undefined,
                    action_type: 'buy',
                    buy_item_id: purchaseItem?.purchase_item_id,
                  })

                  // ビジネスイベントロギング送信
                  if (
                    isTransitionFromCoordinate(
                      screenId ?? ScreenIds.SP,
                      objectId
                    )
                  ) {
                    sendLog({
                      loggedInMemberId: member ? member.id : null,
                      timestamp: timestamp,
                      itemId: item.id,
                      coordinateId: objectId,
                      coordinateMemberId: coordinate ? coordinate.member.id : 0,
                      coordinateMemberShopId: coordinate?.member?.shop
                        ? coordinate.member.shop?.id
                        : null,
                      coordinateItemId: null,
                    })
                  }
                }}
                target="_blank"
                rel="noreferrer"
              >
                <div className="px-[10px] py-[15px] text-[16px] xl:px-5 xl:pb-[10px] xl:pt-[13px] xl:text-[18px]">
                  <span className="xl:hidden">
                    {isZozotownItem
                      ? 'ZOZOTOWNで在庫を見る'
                      : `${purchaseItem.shop.mall_name}で在庫を見る`}
                  </span>
                  <span className="hidden xl:inline">
                    {isZozotownItem
                      ? 'ZOZOTOWNで購入する'
                      : `${purchaseItem.shop.mall_name}で購入する`}
                  </span>
                  <br />
                  <span className="block pt-[9px] text-[13px] xl:pt-1 xl:text-[12px] xl:leading-[1.3]">
                    {isZozotownItem
                      ? `(${purchaseItem.shop?.name}${coupon})`
                      : `(${purchaseItem.shop?.name})`}
                  </span>
                </div>
              </Button>
            </div>
          )
        })}
        {item.search_condition_label && (
          <div className="pt-[10px] xl:pt-3">
            <Button
              href={searchButtonUrl}
              onClick={() => {
                if (item.id !== null && searchButtonUrl !== undefined)
                  sendItemDetailSearchButtonGtmEvent(
                    item.brand.name,
                    searchButtonUrl,
                    item.id
                  )

                postAffiliates({
                  before_screen_id: Number(screenId ?? ScreenIds.SP),
                  content_id: objectId,
                  item_id: item.id ?? undefined,
                  action_type: 'search',
                })

                // ビジネスイベントロギング送信
                if (
                  isTransitionFromCoordinate(screenId ?? ScreenIds.SP, objectId)
                ) {
                  sendLog({
                    loggedInMemberId: member ? member.id : null,
                    timestamp: timestamp,
                    itemId: item.id,
                    coordinateId: objectId,
                    coordinateMemberId: coordinate ? coordinate.member.id : 0,
                    coordinateMemberShopId: coordinate?.member?.shop
                      ? coordinate.member.shop.id
                      : null,
                    coordinateItemId: null,
                  })
                }
              }}
              target="_blank"
              rel="noreferrer"
              color="outlined"
            >
              <div className="px-[10px] py-[15px] text-[16px] xl:px-5 xl:pb-[10px] xl:pt-[13px] xl:text-[18px] xl:text-blue-500">
                ZOZOTOWNで同じアイテムを探す
                <br />
                <span className="block pt-2 text-[13px] leading-[1.2] xl:pt-1 xl:leading-[1.3]">
                  ({item.search_condition_label})
                </span>
              </div>
            </Button>
          </div>
        )}
        {item.purchase_items.length === 0 && !item.search_condition_label && (
          <p className="hidden xl:block xl:pt-[6px] xl:text-[16px] xl:leading-none">
            取り扱いサイトがございません
          </p>
        )}
      </div>
    </section>
  )
}
